import React from 'react'
import './Home.css'
import { Link } from 'react-router-dom'
import Seo from '../../components/Seo/Seo'
import { SEO } from '../../constants/seo'
import Testimonials from '../../components/Testimonials/Testimonials'
import Footer from '../../components/Footer/Footer'
import { useReveal } from '../../hooks/useReveal'
import { trackOutboundBook } from '../../utils/analytics'
import taraHero from '../../images/tara.png'
import taraMeet from '../../images/circleTara.png'

const BOOK_URL =
  'https://www.optimantra.com/optimus/patient/patientaccess/servicesall?mid=1023&pid=NWxtalFOa1M1ZDZwSTNwZ3AzY08vdz09&lid=TmNIZ3FKUFdhVm1YME1lQkMxUEE5QT09&hideFooterInFrame=&hideHeaderInFrame='

const tags = [
  'Chronic disease',
  'Hormone balance',
  'Gut health',
  'Autoimmune support',
  'Nutrition & lifestyle',
  'Root cause care',
]

const features = [
  {
    num: '01',
    title: 'Personalized care',
    body: 'Visits built around your story, goals, and what has—and hasn’t—worked before.',
  },
  {
    num: '02',
    title: 'Root cause focus',
    body: 'We look upstream at drivers of symptoms, not only the diagnosis on the chart.',
  },
  {
    num: '03',
    title: 'Evidence-based medicine',
    body: 'Conventional and functional strategies together, grounded in science and your lived experience.',
  },
  {
    num: '04',
    title: 'True partnership',
    body: 'Collaboration with you—and your existing providers—so nothing works in a silo.',
  },
  {
    num: '05',
    title: 'Lifestyle & nutrition',
    body: 'Practical plans for food, sleep, stress, movement, and targeted support when appropriate.',
  },
  {
    num: '06',
    title: 'Education that empowers',
    body: 'Clear explanations and ongoing education so you can steer your health with confidence.',
  },
]

const Home = () => {
  useReveal()

  return (
    <div className="page__container home__page">
      <Seo title={SEO.home.title} description={SEO.home.description} />
      <section className="home__hero home__reveal" data-reveal aria-labelledby="home-hero-heading">
        <div className="home__inner home__hero__grid">
          <div className="home__hero__copy">
            <p className="home__hero__eyebrow">Board-certified Family Nurse Practitioner</p>
            <h1 id="home-hero-heading" className="home__h1 home__h1--hero">
              <span className="home__hero__line1">Take control</span>
              <span className="home__hero__line2">of your wellness journey.</span>
            </h1>
            <p className="home__hero__intro">
              Do you suffer from a chronic disease? Are you tired of feeling bad? Has traditional
              medicine left you feeling lost? I am a board certified Family Nurse Practitioner who
              believes in the utilization of both conventional and functional medicine strategies in
              order to provide a more comprehensive approach for optimal health.
            </p>
            <div className="home__hero__actions">
              <a
                className="home__btn home__btn--primary"
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundBook(BOOK_URL)}
              >
                Book a free consultation
              </a>
              <Link className="home__btn home__btn--ghost" to="/podcast">
                Listen to the podcast
              </Link>
            </div>
          </div>
          <div className="home__hero__media">
            <div className="home__hero__img-wrap">
              <img
                src={taraHero}
                alt="Tara Quintana with her husband Tom, smiling outdoors"
                width={640}
                height={640}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="home__hero__img"
              />
            </div>
          </div>
        </div>
        <div className="home__hero__tags" aria-label="Focus areas">
          <div className="home__inner">
            <ul className="home__tag-list">
              {tags.map((t) => (
                <li key={t}>
                  <span className="home__tag">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="home__meet home__reveal" data-reveal aria-labelledby="meet-heading">
        <div className="home__inner home__meet__grid">
          <div className="home__meet__media">
            <img
              src={taraMeet}
              alt="Tara Quintana, Family Nurse Practitioner"
              width={400}
              height={400}
              loading="lazy"
              decoding="async"
              className="home__meet__img"
            />
          </div>
          <div className="home__meet__copy">
            <p className="home__meet__eyebrow">About your clinician</p>
            <h2 id="meet-heading" className="home__h2 home__font-display">
              Meet Tara Quintana
            </h2>
            <h3 className="home__h3">Creator of The Functional Nurse Practitioner Podcast.</h3>
            <p>
              Tara is a board certified Family Nurse Practitioner who believes in combining
              conventional and functional medicine strategies for a more comprehensive approach to
              optimal health. She has pursued advanced training to support clients navigating chronic
              illness, hormones, digestion, and the gap between “normal” labs and how you actually
              feel.
            </p>
            <Link className="home__text-link" to="/about">
              Learn more
            </Link>
          </div>
        </div>
      </section>

      <section className="home__together home__reveal" data-reveal aria-labelledby="together-heading">
        <div className="home__inner">
          <h2 id="together-heading" className="home__together__title home__font-display">
            You don&apos;t need to
            <span className="home__together__break"> do this alone.</span>
          </h2>
          <p className="home__together__sub">
            Empowering you on your wellness journey—with clarity, compassion, and a plan that fits
            your life.
          </p>
          <ul className="home__feature-grid">
            {features.map(({ num, title, body }) => (
              <li key={title} className="home__feature-card">
                <span className="home__feature-num" aria-hidden="true">
                  {num}
                </span>
                <h3 className="home__feature-title">{title}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ul>
          <div className="home__quick-links">
            <Link className="home__quick-link" to="/what-is-functional-medicine">
              Our approach
            </Link>
            <Link className="home__quick-link" to="/membership">
              Membership
            </Link>
            <Link className="home__quick-link" to="/about">
              About the practice
            </Link>
          </div>
        </div>
      </section>

      <section
        className="home__testimonials-wrap home__reveal"
        data-reveal
        aria-labelledby="testimonials-heading"
      >
        <div className="home__inner">
          <Testimonials variant="light" />
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Home
